<script setup>
import { computed, ref, watch } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { isoToTime, replaceTimeInISO } from '@/composables/date-time'
import { useReadonlyField } from '@/composables/readonly-form'

const i18n = getI18n()

const props = defineProps({
  modelValue: {
    type: [String, null],
    required: true,
  },
  label: {
    type: String,
    required: true,
  },
  min: {
    type: String,
    required: false,
  },
  end: {
    type: Boolean,
    required: false,
  },
  from: {
    type: Boolean,
    required: false,
  },
  clearable: {
    type: Boolean,
    required: false,
    default: false,
  },
  rules: {
    type: Array,
    required: false,
    default: () => [],
  },
  lazyRules: {
    type: [Boolean, String],
    required: false,
    default: false,
  },
  readonly: {
    type: Boolean,
    required: false,
    default: false,
  },
})

const isReadonly = useReadonlyField(() => props.readonly)

const emit = defineEmits(['update:modelValue'])

const display = ref(isoToTime(props.modelValue))
watch(() => props.modelValue, (v) => { display.value = isoToTime(v) })

const minTime = computed(() => isoToTime(props.min))

// Normalize a partial entry ("13", "13:", "9:5") into a full HH:mm, or null if invalid
const completeTime = (raw) => {
  if (!raw) return null
  const [h, m] = String(raw).split(':')
  const hh = parseInt(h, 10)
  const mm = m ? parseInt(m, 10) : 0
  if (isNaN(hh) || hh > 23 || isNaN(mm) || mm > 59) return null
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`
}

const emitTime = (time) => emit('update:modelValue', time ? replaceTimeInISO(props.modelValue, time) : null)

const onInput = (val) => {
  display.value = val
  if (/^\d{2}:\d{2}$/.test(val)) emitTime(val)
}

const onBlur = () => {
  const time = completeTime(display.value)
  display.value = time
  emitTime(time)
}

const onClear = () => {
  display.value = null
  emitTime(null)
}

const validationRules = computed(() => [
  ...props.rules,
  ...(props.min
    ? [val => !val || !minTime.value || val >= minTime.value || i18n.t('error.endBeforeStart')]
    : []),
])
</script>
<template>
  <q-input
    :model-value="display"
    :label="label"
    mask="time"
    :readonly="isReadonly"
    :clearable="clearable && !isReadonly"
    :rules="validationRules.length ? validationRules : undefined"
    :lazy-rules="lazyRules"
    @update:model-value="onInput"
    @blur="onBlur"
    @clear="onClear"
  />
</template>
